import { FilterModel, FilterOption } from '../../model/ui/filter.model';
import { FilterType } from '../../model/ui/filter.type';
import { ListSortOption } from '../../model/ui/list-sort-option';
import { RecordColumn } from '../../model/ui/record-list.type';

type Translate = (key: string) => string;

export const isFilterApplied = (filter: FilterModel): boolean =>
  typeof filter.value === 'string' &&
  filter.value.trim() !== '' &&
  filter.value !== filter.defaultValue;

export const getFilterLabel = (
  filter: FilterModel,
  translate: Translate
): string => (filter.label ? translate(filter.label) : filter.fieldName);

export const getFilterOptionLabel = (
  option: FilterOption,
  translate: Translate
): string => (option.translate ? translate(option.label) : option.label);

export const getFilterValueLabel = (
  filter: FilterModel,
  translate: Translate,
  value: unknown = filter.value
): string => {
  if (filter.type === FilterType.slider) {
    return `${value}`.replace(',', ' – ');
  }

  const selectedOption = filter.options?.find(
    (option) => option.value === value
  );

  return selectedOption
    ? getFilterOptionLabel(selectedOption, translate)
    : `${value}`;
};

export const matchesFilterSearch = (
  option: FilterOption,
  search: string,
  translate: Translate
): boolean => {
  const needle = search.trim().toLowerCase();

  return (
    !needle ||
    [getFilterOptionLabel(option, translate), option.secondaryLabel ?? ''].some(
      (text) => text.toLowerCase().includes(needle)
    )
  );
};

export const parseRangeFilterValue = (
  value: string,
  min: number,
  max: number
): [number, number] => {
  const [from, to] = value.split(',').map(Number);

  return value && Number.isFinite(from) && Number.isFinite(to)
    ? [from, to]
    : [min, max];
};

export const formatRangeFilterValue = (
  [from, to]: number[],
  min: number,
  max: number
): string => (from === min && to === max ? '' : `${from},${to}`);

interface FilterLookup {
  query: {
    data?: string[];
    isPending: boolean;
    isError: boolean;
    refetch: () => unknown;
  };
  fixedOptions?: FilterOption[];
}

const getLookupOptionsState = ({
  query,
}: FilterLookup): FilterModel['optionsState'] => {
  if (query.isPending) {
    return { status: 'pending', message: 'common.listFilters.loadingOptions' };
  }

  return query.isError
    ? {
        status: 'error',
        message: 'common.listFilters.optionsFailed',
        onRetry: () => query.refetch(),
      }
    : undefined;
};

export const withFilterLookupOptions = (
  filters: FilterModel[],
  lookups: Record<string, FilterLookup>
): FilterModel[] =>
  filters.map((filter) => {
    const lookup = lookups[filter.fieldName];

    return lookup
      ? {
          ...filter,
          options: [
            ...(lookup.fixedOptions ?? []),
            ...(lookup.query.data ?? []).map((value) => ({
              label: value,
              value,
            })),
          ],
          optionsState: getLookupOptionsState(lookup),
        }
      : filter;
  });

export const getColumnSortOptions = <T>(
  columns: RecordColumn<T>[],
  translate: (key: string, options: { field: string }) => string
): ListSortOption[] =>
  columns.flatMap(({ sortKey, header }) =>
    sortKey
      ? (['asc', 'desc'] as const).map((order_dir) => ({
          order_by: sortKey,
          order_dir,
          label: translate(`common.listSort.${order_dir}`, { field: header }),
        }))
      : []
  );
